import React, { useEffect, useRef, useState } from 'react';
import Header from '../../../components/header';
import Layout from '../../../components/layout';
import { AgGridReact } from 'ag-grid-react';
import Icon from '../../../components/icons';
import Drawer from '../../../components/Drawer';
import { Dropdown, Menu } from '../../../components/Dropdown';
import Button from '../../../components/elements/button';
import { DATE_FORMAT, PAGE_SIZE } from '../../../_common/constant';
import Pagination from '../../../components/pagination';
import Search from '../../../components/search';
import { downloadExcel, downloadPDF, showLocalDate } from '../../../_common/functions';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import toast from 'react-hot-toast';
import { useStockAdjustmentApi } from '../../../store/api/hooks/stockAdjustmentApiHook';
import { useSpearPartsApi } from '../../../store/api/hooks/spearPartsApiHook';
import Currency from '../../../components/currency';
import moment from 'moment';
import { filterConfigType } from '../../../_config/types';
import Filters from '../../../components/filters';

const StockTransactionReportList: React.FC = () => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [searchParam, setSearchParam] = useState<string>('');
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [rowData, setrowData] = useState<any>(null);
    const [personalStock, setPersonalStock] = useState<any[]>([]);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const stockAdjustmentAPI = useStockAdjustmentApi();
    const spearPartsAPI = useSpearPartsApi();

    // Filter states
    const filtersRef: any = useRef();
    const [filters, setFilters] = useState<any>({});
    const [filtersApplied, setFiltersApplied] = useState<any>({ generalFilter: false });
    const [filtersInitialized, setFiltersInitialized] = useState<boolean>(false);

    // Load filters from localStorage on component mount
    useEffect(() => {
        const savedFilters = localStorage.getItem('stockTransactionReportFilters');
        if (savedFilters) {
            try {
                const parsedFilters = JSON.parse(savedFilters);
                setFilters(parsedFilters);
            } catch (error) {
                console.error('Error parsing saved filters:', error);
                setFilters({});
            }
        } else {
            setFilters({});
        }
        setFiltersInitialized(true);
    }, []);

    // Save filters to localStorage when filters change
    useEffect(() => {
        if (Object.keys(filters).length > 0) {
            localStorage.setItem('stockTransactionReportFilters', JSON.stringify(filters));
        }
    }, [filters]);

    // Async function to load spare parts for Part Code filter
    const loadSparePartsOptions = async (searchParam: string): Promise<any> => {
        return new Promise((resolve) => {
            const payload = {
                pageSize: 50, // Limit results for dropdown
                pageNumber: 1,
                searchParam: searchParam
            };
            
            spearPartsAPI.getSpearPartsList(
                payload,
                (response: any) => {
                    const options = response.sparePartsList?.map((part: any) => ({
                        label: `${part.partsCode} - ${part.sparePartsName}`,
                        value: part.partsCode
                    })) || [];
                    resolve(options);
                },
                (error: any) => {
                    console.error('Error loading spare parts:', error);
                    resolve([]);
                }
            );
        });
    };

    // Filter configuration
    const filterConfig: filterConfigType[] = [
        { 
            name: 'Part Code', 
            type: 'dropdown', 
            key: 'partCode',
            async: loadSparePartsOptions,
            isMulti: false
        },
        { name: 'From Date', type: 'date', key: 'fromDate' },
        { name: 'To Date', type: 'date', key: 'toDate' },
    ];

    const onGridReady = (params: any) => {
    };
    const onRowClicked = (event: any) => {
        if (!event.event.target.closest('input[type="checkbox"]')) {
            event.node.setSelected(false);
        }
    };

    const columnDefs = [
        { headerName: 'Date', field: 'date', headerClass: 'bg-[#f0f0f0]', pinned: 'left', width: 140, 
            cellRenderer: (data: any) => data.value ? data.value : '--', 
            headerTooltip: 'Date' 
        },
        { headerName: 'Part Code', field: 'partCode', headerClass: 'bg-[#f0f0f0]', pinned: 'left', width: 200, cellRenderer: titleCell, headerTooltip: 'Part Code' },
        { headerName: 'In', field: 'in', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 100, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? data.value : '--',
            headerTooltip: 'In Quantity' 
        },
        { headerName: 'In Unit Cost', field: 'inUnitCost', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 120, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'In Unit Cost' 
        },
        { headerName: 'In Total', field: 'inTotal', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 120, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'In Total' 
        },
        { headerName: 'Out', field: 'out', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 100, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? data.value : '--',
            headerTooltip: 'Out Quantity' 
        },
        { headerName: 'Out Unit Cost', field: 'outUnitCost', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 130, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'Out Unit Cost' 
        },
        { headerName: 'Out Total', field: 'outTotal', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 120, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'Out Total' 
        },
        { headerName: 'Returned', field: 'returned', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 130, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? data.value : '--',
            headerTooltip: 'Returned' 
        },
        { headerName: 'Returned Unit Cost', field: 'returnedUnitCost', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 180, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'Returned Unit Cost' 
        },
        { headerName: 'Returned Total', field: 'returnedTotal', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 150, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? data.value : '--',
            headerTooltip: 'Returned Total' 
        },
        { headerName: 'Used', field: 'used', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 100, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? data.value : '--',
            headerTooltip: 'Used Quantity' 
        },
        { headerName: 'Used Unit Cost', field: 'usedUnitCost', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 130, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'Used Unit Cost' 
        },
        { headerName: 'Used Total', field: 'usedTotal', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 120, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'Used Total' 
        },
        { headerName: 'Stock Balance', field: 'stockBalance', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 130, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? data.value : '--',
            headerTooltip: 'Stock Balance' 
        },
        { headerName: 'Avg. Unit Cost', field: 'avgUnitCost', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 130, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'Average Unit Cost' 
        },
        { headerName: 'Stock Value', field: 'stockValue', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 130, 
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => data.value ? <Currency currency="USD" value={data.value}></Currency> : '--',
            headerTooltip: 'Stock Value' 
        },
        { headerName: 'Reason', field: 'reason', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'Reason' },
        { headerName: 'By', field: 'by', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'Transaction By' },
    ];

    const GetStockTransactionReportList = () => {
        let payload: any = {
            pageSize: PAGE_SIZE,
            pageNumber: currentPage,
            searchParam: searchParam,
        };

        // Add filters to payload
        if (filters) {
            Object.keys(filters).map((key: any) => {
                if (filters[key]) {
                    payload[key] = filters[key];
                }
            });
        }

        setrowData(null);
        
        stockAdjustmentAPI.GetStockTransactionReport(payload, (resp: any) => {
            setrowData(resp.rows ?? []);
            setTotalRecords(resp.totalRecord);
            setPersonalStock(resp.personalStock ?? []);
        }, (error: any) => {
            console.log('error =>', error);
            toast.error(error);
        });

    };

    useEffect(() => {
        if (filtersInitialized) {
            GetStockTransactionReportList();
        }
        
        const hasActiveFilters = filterConfig.some((filter) => filters[filter.key]);
        setFiltersApplied({
            generalFilter: hasActiveFilters,
        });
    }, [currentPage, searchParam, filters, filtersInitialized]);

    const ExportStockTransactionReportExcel = async () => {
        const payload: any = {
            pageSize: 0,
            pageNumber: 1,
        };
        if (filters) {
            Object.keys(filters).map((key: any) => {
                if (filters[key]) {
                    payload[key] = filters[key];
                }
            });
        }
        
        setIsExporting(true);
        stockAdjustmentAPI.ExportStockTransactionReportcsv(
            payload,
            (resp: any) => {
                downloadExcel(resp, 'stock_transaction_report');
                toast.success('Stock transaction report exported successfully!');
                setIsExporting(false);
            },
            (error: any) => {
                console.log("error =>", error);
                toast.error(error || 'Failed to export stock transaction report');
                setIsExporting(false);
            }
        );
    };

    return (
        <Layout>
            <Header title="Stock Transaction Report List" back="/reports" >
                <Header.Right>
                    {/* Excel export button is intentionally excluded as requested */}
                    <Button 
                        theme='primary' 
                        onClick={ExportStockTransactionReportExcel}
                        disabled={isExporting}
                    >
                        {isExporting ? 'Exporting...' : 'Export Excel'}
                    </Button>
                </Header.Right>
            </Header>
            <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
                <Pagination
                    perPage={PAGE_SIZE}
                    totalRecords={totalRecords}
                    onPageChange={setCurrentPage}
                    currentPage={currentPage}
                />
                {/* <Search onSearch={setSearchParam} placeholder='Search by part name, code or transaction ID' /> */}
            </div>
            <div className='bg-[#FAFAFA]'>
                <div className='flex gap-4 px-5 py-2 items-center'>
                    <Button theme='light' onClick={() => filtersRef.current.showModal()}>Filter <Icon name='filter' size={1.40} className={filtersApplied.generalFilter ? "text-blue-700" : ""} /></Button>
                    <p className='text-primary font-semibold text-sm cursor-pointer' onClick={() => { 
                        setFilters({});
                        localStorage.removeItem('stockTransactionReportFilters');
                    }} >Clear Filters</p>
                </div>

                <div className="ag-theme-alpine mx-4 w-full h-1/2">
                    <AgGridReact
                        columnDefs={columnDefs as any} // Pass columnDefs
                        rowData={rowData || []} // Pass rowData
                        rowSelection="multiple"
                        onGridReady={onGridReady}
                        pagination={false}
                        domLayout="autoHeight"
                        onRowClicked={onRowClicked}
                        loadingOverlayComponent={Loader}
                        noRowsOverlayComponent={() => (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center text-gray-500">
                                    <div className="text-lg font-medium">No Data Found</div>
                                </div>
                            </div>
                        )}
                    />
                </div>

                
            </div>

            <Filters _ref={filtersRef} config={filterConfig} filters={filters} onUpdate={setFilters} />
        </Layout>
    );
};
export default StockTransactionReportList;